import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { Language, TranslationKey } from '../i18n';
import { translations } from '../i18n';
import { langToSlug } from '../hooks/useLocalePath';

interface LanguageContextValue {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: TranslationKey) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

interface LanguageProviderProps {
    children: React.ReactNode;
    forcedLanguage?: Language;
}

export function LanguageProvider({ children, forcedLanguage }: LanguageProviderProps) {
    const [language, setLanguageState] = useState<Language>(forcedLanguage ?? 'EN');

    // Sync with forcedLanguage from URL
    useEffect(() => {
        if (forcedLanguage && forcedLanguage !== language) {
            setLanguageState(forcedLanguage);
        }
    }, [forcedLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const htmlLang = language === 'EN' ? 'en' : language === 'PT' ? 'pt-BR' : 'es';
        if (typeof document !== 'undefined') {
            document.documentElement.lang = htmlLang;
        }
        try { localStorage.setItem('perceptya-lang', language); } catch { /* noop */ }
    }, [language]);

    const t = useCallback((key: TranslationKey): string => {
        return translations[language][key] ?? key;
    }, [language]);

    const contextValue: LanguageContextValue = {
        language,
        setLanguage: setLanguageState,
        t,
    };

    return (
        <LanguageContext.Provider value={contextValue}>
            {children}
        </LanguageContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLanguage(): LanguageContextValue {
    const ctx = useContext(LanguageContext);
    if (!ctx) throw new Error('useLanguage must be used inside LanguageProvider');
    return ctx;
}

/** Hook that returns a function to switch language via URL navigation */
// eslint-disable-next-line react-refresh/only-export-components
export function useLanguageNav() {
    const navigate = useNavigate();
    const location = useLocation();

    return useCallback((newLang: Language) => {
        const slug = langToSlug(newLang);
        // Replace the lang segment: /en/services → /pt-br/services
        const pathParts = location.pathname.split('/');
        pathParts[1] = slug; // [0] is empty string before first /
        navigate(pathParts.join('/') || `/${slug}`, { replace: false });
    }, [navigate, location.pathname]);
}
